/**
 * Inventory CSV Loader
 * P20250925-125 — Master inventory with commerce data
 */

import { z } from 'zod';

const inventorySchema = z.object({
  domain: z.string().min(1),
  slug: z.string().min(1),
  tld: z.string().min(1),
  bin_usd: z.coerce.number().positive(),
  lease_term_months: z.coerce.number().int().positive(),
  status: z.enum(['available', 'sold', 'reserved']),
  category: z.string().optional(),
  keywords: z.string().optional(),
  notes: z.string().optional(),
});

export type InventoryRecord = z.infer<typeof inventorySchema>;

let cachedInventory: InventoryRecord[] | null = null;
let cacheTime: number = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Load and parse inventory CSV
 */
export async function loadInventory(): Promise<InventoryRecord[]> {
  const now = Date.now();

  // Return cached if fresh
  if (cachedInventory && now - cacheTime < CACHE_TTL) {
    return cachedInventory;
  }

  try {
    const response = await fetch('/data/inventory.csv');
    if (!response.ok) throw new Error('Failed to fetch inventory');

    const text = await response.text();
    const lines = text.trim().split('\n');
    const headers = lines[0].split(',').map((h) => h.trim());

    const records: InventoryRecord[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',');
      const row: Record<string, string> = {};

      headers.forEach((header, idx) => {
        row[header] = values[idx]?.trim() || '';
      });

      try {
        const parsed = inventorySchema.parse(row);
        records.push(parsed);
      } catch (err) {
        console.warn(`[Inventory] Invalid row ${i}:`, err);
      }
    }

    cachedInventory = records;
    cacheTime = now;

    return records;
  } catch (error) {
    console.error('[Inventory] Load error:', error);
    return cachedInventory || [];
  }
}

/**
 * Get domain by hostname
 */
export async function getDomainByHost(host: string): Promise<InventoryRecord | null> {
  const inventory = await loadInventory();

  // Normalize host (strip www, lowercase)
  const normalized = host.toLowerCase().replace(/^www\./, '');

  return inventory.find((r) => r.domain.toLowerCase() === normalized) || null;
}

/**
 * Get domain by slug
 */
export async function getDomainBySlug(slug: string): Promise<InventoryRecord | null> {
  const inventory = await loadInventory();
  return inventory.find((r) => r.slug === slug) || null;
}

/**
 * Get similar domains
 */
export async function getSimilarDomains(
  record: InventoryRecord,
  limit: number = 6,
): Promise<InventoryRecord[]> {
  const inventory = await loadInventory();

  // Score by: same TLD (+3), same category (+2), keyword overlap (+1 each)
  const scored = inventory
    .filter((r) => r.slug !== record.slug && r.status === 'available')
    .map((r) => {
      let score = 0;
      if (r.tld === record.tld) score += 3;
      if (r.category === record.category) score += 2;

      const recordKw = (record.keywords || '').toLowerCase().split(' ');
      const candidateKw = (r.keywords || '').toLowerCase().split(' ');
      const overlap = recordKw.filter((k) => candidateKw.includes(k)).length;
      score += overlap;

      return { record: r, score };
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((s) => s.record);

  return scored;
}

/**
 * Price band helper
 */
export function priceBand(record: InventoryRecord): string {
  const price = record.bin_usd;
  if (price < 5000) return 'under-5k';
  if (price < 10000) return '5k-10k';
  if (price < 25000) return '10k-25k';
  return '25k-plus';
}

/**
 * Clear cache (for testing)
 */
export function clearInventoryCache(): void {
  cachedInventory = null;
  cacheTime = 0;
}
